import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Box, IconProps, palette } from '@landr/maestro';
import BackgroundImageFromUrl from 'components/BackgroundImageFromUrl/BackgroundImageFromUrl';
import ExclusivePackBadge from 'components/ExclusiveCardBadge/ExclusiveCardBadge';
import TakenDownBadge from 'components/TakenDownBadge/TakenDownBadge';
import IconArtwork from 'components/CommonUi/IconArtwork/IconArtwork';
import LetterArtwork from 'components/CommonUi/LetterArtwork/LetterArtwork';
import { ArtworkCustomSize } from 'components/Artworks/types';

const DefaultArtworkSize = 220;

const ArtworkWrapper = styled(Box)<{
    isRounded: boolean;
    imageWidthSize: number;
    imageHeightSize: number;
}>`
    position: relative;
    overflow: hidden;
    background-color: ${palette('neutral.800')};
    width: ${({ imageWidthSize }) => `${imageWidthSize}px`};
    height: ${({ imageHeightSize }) => `${imageHeightSize}px`};
    max-width: 100%;
    max-height: 100%;

    ${({ isRounded, theme }): string => `
        border-radius: ${isRounded ? theme.radii.round : theme.radii.base};
    `};
`;

export interface ArtworkProps {
    isLoading?: boolean;
    isExclusive?: boolean;
    isTakenDown?: boolean;
    isRounded?: boolean;
    imageUrl?: string;
    fallbackName?: string;
    fallbackIcon?: React.ComponentType<IconProps>;
    alt: string;
    imageSize?: number | ArtworkCustomSize;
    loading?: 'lazy' | 'eager';
}

const Artwork = ({
    isLoading = false,
    isExclusive = false,
    isTakenDown = false,
    isRounded = false,
    fallbackIcon,
    fallbackName,
    imageUrl,
    alt,
    imageSize = DefaultArtworkSize,
    loading,
}: ArtworkProps) => {
    const fallback = useMemo(() => {
        if (fallbackIcon) {
            return <IconArtwork icon={fallbackIcon} />;
        }
        if (fallbackName) {
            return <LetterArtwork name={fallbackName} />;
        }
    }, [fallbackName, fallbackIcon]);

    const imageWith = imageSize instanceof Array ? imageSize[0] : imageSize;
    const imageHeight = imageSize instanceof Array ? imageSize[1] : imageSize;

    return (
        <ArtworkWrapper
            data-component="Artwork"
            isRounded={isRounded}
            imageWidthSize={imageWith}
            imageHeightSize={imageHeight}
        >
            <BackgroundImageFromUrl
                isLoading={isLoading}
                aria-label={alt}
                alt={alt}
                imageSize={imageSize}
                imageUrl={imageUrl}
                fallback={fallback}
                loading={loading}
            />
            {isExclusive && <ExclusivePackBadge isAbsolute={true} />}
            {isTakenDown && <TakenDownBadge isAbsolute={true} />}
        </ArtworkWrapper>
    );
};

export default Artwork;
